import React, { useState, useRef, useCallback, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import { loadTable, addRow, saveRow, isApiReady, checkApi, getTableData, saveTableData, getNextId } from '../../db-viewer/data/dataAccess';
import { storeDocBlob, isServerKey } from '../utils/docStorage';
import { confirmDiscardChanges, setFormDirty, registerSaveHandler, submitSaver } from '../../../lib/unsavedChanges';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import { extractFilingFields, resolveDocuTypeId, ExtractEngine } from '../utils/documentExtract';
import { sha256Hex, buildContentTokens, contentSignatureFromTokens } from '../utils/documentFingerprint';
import ConvertToCsvTab from './ConvertToCsvTab';
import './UploadDocsPage.css';

declare global {
  interface Window {
    CompressionStream: any;
  }
  var CompressionStream: {
    new(format: string): {
      readable: ReadableStream<Uint8Array>;
      writable: WritableStream<Uint8Array>;
    };
  } | undefined;
}

// =============================================
// Types
// =============================================
type UploadTabKey = 'receipts-docs' | 'receipts-separate' | 'convert-csv';

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status: 'queued' | 'uploading' | 'done' | 'error';
  progress: number;
  errorMsg?: string;
  addedAt: string;
  storedSize?: number;
  pagesCreated?: number;
  convertedFrom?: string;
}

interface ClientOption {
  id: number;
  name: string;
  type: 'personal' | 'family' | 'business';
  ownerId: number | null;
}

// =============================================
// Constants
// =============================================
const TABS: { key: UploadTabKey; label: string; disabled?: boolean }[] = [
  { key: 'receipts-docs',     label: 'Upload Receipts / Docs' },
  { key: 'receipts-separate', label: 'Separate & Upload Receipts / Docs' },
  { key: 'convert-csv',       label: 'Upload to Convert to .csv file' },
];

const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg'];
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];
const ACCEPT_INPUT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

const VALID_MIME_TYPES: Record<string, string[]> = {
  '.pdf':  ['application/pdf'],
  '.png':  ['image/png'],
  '.jpg':  ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
};

const EXPECTED_FORMAT: Record<string, string> = {
  '.pdf': 'pdf', '.png': 'png', '.jpg': 'jpeg', '.jpeg': 'jpeg',
};

const TAB_INFO: Record<UploadTabKey, { title: string; description: string; hint: string; acceptLabel: string }> = {
  'receipts-docs': {
    title: 'Upload Receipts / Docs',
    description: 'Upload PDF or image files with either a single page receipt or a multiple page receipt. We won\'t separate pages in uploaded file as individual receipts.',
    hint: 'Supported formats: PDF, PNG, JPEG/JPG. Image files are automatically converted to PDF before storage.\nIf your file contains multiple receipts or docs then use the "Separate & Upload" tab to split each page into its own receipt.\nExamples of single page receipt is a receipt for gas filled for car. In this case, receipt starts and ends on one page.\nExample of one multi-page document will be a grocery invoice for a restaurant where 100s of items are received and the invoice will run over multiple pages.',
    acceptLabel: 'PDF, PNG, JPEG',
  },
  'receipts-separate': {
    title: 'Separate & Upload Receipts / Docs',
    description: 'Upload a PDF or image — each page of a PDF will be separated and saved as an individual document for processing.',
    hint: 'Use this when you have scanned multiple receipts into one PDF. Each page will be split into its own receipt during processing.\nFor example, if you scan 10 gas receipts into one PDF, this will create 10 individual receipts.\nImage files (PNG, JPEG) are converted to a single-page PDF. Supported formats: PDF, PNG, JPEG/JPG.',
    acceptLabel: 'PDF, PNG, JPEG',
  },
  'convert-csv': {
    title: 'Upload to Convert to .csv file',
    description: 'Upload a bank or credit-card statement (PDF, PNG, or JPEG) — the transactions are extracted into an editable table you can review, export as CSV, and import into bookkeeping.',
    hint: 'Pick an extraction engine below. Free · Browser costs nothing (digital PDFs are read instantly; photos/scans use slower OCR). Claude API gives the best accuracy on scans/photos. Review the rows before importing.',
    acceptLabel: 'PDF, PNG, JPEG',
  },
};

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

// =============================================
// Utility Functions
// =============================================
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function nowTimestamp(): string {
  return new Date().toISOString().replace('T', ' ').substring(0, 19);
}

function getFileExtension(filename: string): string {
  return '.' + (filename.split('.').pop()?.toLowerCase() || '');
}

// =============================================
// File Validation — magic bytes
// =============================================
async function validateFileHeader(file: File): Promise<{ valid: boolean; format: string | null }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arr = new Uint8Array(reader.result as ArrayBuffer);
      if (arr.length < 3) { resolve({ valid: false, format: null }); return; }

      // PDF: %PDF
      if (arr.length >= 4 &&
          arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46) {
        resolve({ valid: true, format: 'pdf' }); return;
      }
      // PNG: 89 50 4E 47
      if (arr.length >= 4 &&
          arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47) {
        resolve({ valid: true, format: 'png' }); return;
      }
      // JPEG: FF D8 FF
      if (arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF) {
        resolve({ valid: true, format: 'jpeg' }); return;
      }

      resolve({ valid: false, format: null });
    };
    reader.onerror = () => resolve({ valid: false, format: null });
    reader.readAsArrayBuffer(file.slice(0, 8));
  });
}

// =============================================
// Compression — gzip + base64
// =============================================
async function compressBytesToBase64(data: Uint8Array): Promise<{ base64: string; compressedSize: number }> {
  if (typeof CompressionStream !== 'undefined') {
    const cs = new CompressionStream('gzip');
    const writer = cs.writable.getWriter();
    writer.write(data);
    writer.close();
    const chunks: Uint8Array[] = [];
    const reader = cs.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
    const compressed = new Uint8Array(totalLen);
    let offset = 0;
    for (const chunk of chunks) {
      compressed.set(chunk, offset);
      offset += chunk.length;
    }
    let binary = '';
    for (let i = 0; i < compressed.length; i++) {
      binary += String.fromCharCode(compressed[i]);
    }
    return { base64: btoa(binary), compressedSize: compressed.length };
  }
  // Fallback: raw base64 (no compression)
  let binary = '';
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return { base64: btoa(binary), compressedSize: data.length };
}

async function compressFileToBase64(file: File): Promise<{ base64: string; compressedSize: number }> {
  return compressBytesToBase64(new Uint8Array(await file.arrayBuffer()));
}

// =============================================
// Image → PDF conversion (pdf-lib)
// =============================================
async function convertImageToPdfBytes(file: File): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const imageBytes = new Uint8Array(await file.arrayBuffer());
  const ext = getFileExtension(file.name);

  const image = ext === '.png'
    ? await pdfDoc.embedPng(imageBytes)
    : await pdfDoc.embedJpg(imageBytes);

  const page = pdfDoc.addPage([image.width, image.height]);
  page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });

  return pdfDoc.save();
}

// =============================================
// PDF page splitting (pdf-lib)
// =============================================
async function splitPdfIntoPages(pdfBytes: Uint8Array): Promise<Uint8Array[]> {
  const srcDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const pageCount = srcDoc.getPageCount();
  const pages: Uint8Array[] = [];

  for (let i = 0; i < pageCount; i++) {
    const newDoc = await PDFDocument.create();
    const [copiedPage] = await newDoc.copyPages(srcDoc, [i]);
    newDoc.addPage(copiedPage);
    pages.push(await newDoc.save());
  }

  return pages;
}

// =============================================
// Client / User helpers
// =============================================
function getCurrentUserFromData(usersData: Record<string, any>[]): { user_id: number; owner_id: number | null; role: string } | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null ? null : Number(userRow.owner_id),
      role: session.role || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getClientsFromData(
  usersData: Record<string, any>[],
  businessesData: Record<string, any>[],
  currentUser: { user_id: number; owner_id: number | null; role: string }
): ClientOption[] {
  const clients: ClientOption[] = [];
  const accountOwnerId = currentUser.owner_id == null
    ? currentUser.user_id
    : currentUser.owner_id;

  try {
    for (const u of usersData) {
      if (u.is_deleted === 1) continue;
      const uid = Number(u.user_id);
      const oid = u.owner_id == null ? null : Number(u.owner_id);
      if (uid !== accountOwnerId && oid !== accountOwnerId) continue;
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.user_id}`;
      const isOwner = oid == null;
      clients.push({ id: uid, name, type: isOwner ? 'personal' : 'family', ownerId: oid });
    }
  } catch { /* ignore */ }
  try {
    for (const b of businessesData) {
      if (b.is_deleted === 1) continue;
      if (Number(b.owner_id) !== accountOwnerId) continue;
      const name = b.busi_legal_name || b.busi_operating_name || `Business #${b.business_id}`;
      clients.push({ id: b.business_id + 10000, name, type: 'business', ownerId: b.owner_id == null ? null : Number(b.owner_id) });
    }
  } catch { /* ignore */ }
  return clients;
}

// =============================================
// Main Component
// =============================================
const UploadDocsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<UploadTabKey>(() => {
    const saved = sessionStorage.getItem('akt_upload_active_tab');
    if (saved === 'receipts-docs' || saved === 'receipts-separate' || saved === 'convert-csv') return saved;
    return 'receipts-docs';
  });
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [notes, setNotes] = useState('');
  const [showFirstTimeHint, setShowFirstTimeHint] = useState(true);
  const [documents, setDocuments] = useState<Record<string, any>[]>([]);
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [docuTypes, setDocuTypes] = useState<Record<string, any>[]>([]);
  // Engine used to auto-fill the Filing fields on upload. Default 'auto' = read
  // everything free in-browser first and escalate only the weak results to AI
  // (cost-optimal). Persisted under its own key (separate from the Convert-to-CSV
  // tab's akt_extract_engine, which only knows local/claude).
  const [engine, setEngine] = useState<ExtractEngine>(() => {
    try {
      const saved = localStorage.getItem('akt_doc_extract_engine');
      if (saved === 'auto' || saved === 'local' || saved === 'claude' || saved === 'self_hosted') return saved;
    } catch { /* ignore */ }
    return 'auto';
  });
  const changeEngine = useCallback((e: ExtractEngine) => {
    setEngine(e);
    try { localStorage.setItem('akt_doc_extract_engine', e); } catch { /* ignore */ }
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCountRef = useRef(0);
  // Sort for the "Uploaded Documents" history table (default: newest upload first).
  const [sortField, setSortField] = useState<'filename' | 'page_count' | 'upload_date'>('upload_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Delete-confirmation modal target (null = closed). childDocs = separated pages
  // that cascade-delete with an original.
  const [deleteTarget, setDeleteTarget] = useState<{ doc: any; childDocs: any[] } | null>(null);

  // Load all data from API (or localStorage fallback) on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await checkApi();
      const [users, businesses, docs, dtypes] = await Promise.all([
        loadTable('users'),
        loadTable('user_businesses'),
        loadTable('dms_documents'),
        loadTable('acctg_docu_type'),
      ]);
      if (cancelled) return;
      setUsersData(users);
      setBusinessesData(businesses);
      setDocuments(docs);
      setDocuTypes(dtypes);
      const cu = getCurrentUserFromData(users);
      if (cu) {
        setClients(getClientsFromData(users, businesses, cu));
      }
      setDataLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const tabInfo = TAB_INFO[activeTab];
  const isDisabledTab = TABS.find(t => t.key === activeTab)?.disabled;

  // Reload documents when tab changes
  useEffect(() => {
    sessionStorage.setItem('akt_upload_active_tab', activeTab);
    if (dataLoaded) {
      (async () => {
        const docs = await loadTable('dms_documents');
        setDocuments(docs);
      })();
    }
  }, [activeTab, dataLoaded]);

  // ---- Drag & Drop ----
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCountRef.current++;
    if (dragCountRef.current === 1) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCountRef.current--;
    if (dragCountRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
  }, []);

  // ---- File Validation ----
  const validateAndAddFiles = useCallback(async (fileList: FileList | File[]) => {
    const newFiles: UploadedFile[] = [];
    const len = fileList instanceof FileList ? fileList.length : fileList.length;

    for (let i = 0; i < len; i++) {
      const file = fileList instanceof FileList ? fileList.item(i) : fileList[i];
      if (!file) continue;

      const ext = getFileExtension(file.name);

      // Extension check
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `Unsupported format (${ext}). Accepted: PDF, PNG, JPEG/JPG.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      // MIME type check
      if (file.type) {
        const validMimes = VALID_MIME_TYPES[ext] || [];
        if (validMimes.length > 0 && !validMimes.includes(file.type)) {
          newFiles.push({
            id: generateId(), file, name: file.name, size: file.size, type: file.type,
            status: 'error', progress: 0,
            errorMsg: `MIME type mismatch (${file.type} for ${ext}). File may be renamed.`,
            addedAt: new Date().toISOString(),
          });
          continue;
        }
      }

      // Size check
      if (file.size > MAX_FILE_SIZE) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `File too large (${formatFileSize(file.size)}). Max ${formatFileSize(MAX_FILE_SIZE)} per file.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      // Magic bytes check
      const { valid, format } = await validateFileHeader(file);
      if (!valid) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `File header check failed — not a valid ${ext.substring(1).toUpperCase()} file.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      // Cross-check: magic bytes format should match extension
      if (format !== EXPECTED_FORMAT[ext]) {
        newFiles.push({
          id: generateId(), file, name: file.name, size: file.size, type: file.type,
          status: 'error', progress: 0,
          errorMsg: `File content is ${format?.toUpperCase()} but extension is ${ext}. Please rename correctly.`,
          addedAt: new Date().toISOString(),
        });
        continue;
      }

      const isImage = IMAGE_EXTENSIONS.includes(ext);
      newFiles.push({
        id: generateId(), file, name: file.name, size: file.size, type: file.type,
        status: 'queued', progress: 0, addedAt: new Date().toISOString(),
        convertedFrom: isImage ? ext.substring(1).toUpperCase() : undefined,
      });
    }

    setFiles(prev => [...prev, ...newFiles]);
    if (newFiles.length > 0) setFormDirty(true);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCountRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      validateAndAddFiles(e.dataTransfer.files);
    }
  }, [validateAndAddFiles]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Snapshot to a real array BEFORE clearing the input. e.target.files is a
      // live FileList — clearing e.target.value empties it, and since
      // validateAndAddFiles awaits (header checks) it would then read every file
      // after the first as null. Array.from() detaches a stable copy.
      const picked = Array.from(e.target.files);
      e.target.value = '';
      validateAndAddFiles(picked);
    }
  }, [validateAndAddFiles]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const clearAllFiles = useCallback(() => {
    setFiles([]);
    setFormDirty(false);
  }, []);

  // ---- Upload: convert → split (tab 2) → compress → save ----
  const uploadFiles = useCallback(async (explicitQueue?: UploadedFile[]) => {
    // explicitQueue = the exact batch to upload (used by the auto-upload effect so a
    // mid-upload drop isn't double-processed). A non-array arg (e.g. the Upload
    // button's click event) falls back to "all currently queued".
    const queued = Array.isArray(explicitQueue) ? explicitQueue : files.filter(f => f.status === 'queued');
    if (queued.length === 0) { setFormDirty(false); return; }

    const currentUser = getCurrentUserFromData(usersData);
    const accountOwnerId = currentUser?.owner_id ?? currentUser?.user_id ?? 1;
    const docUserId = selectedClient && selectedClient < 10000
      ? selectedClient
      : currentUser?.user_id ?? 1;

    // Resolve owner_type + owner_id from selectedClient for pre-populating Process Receipts
    let docOwnerType: string | null = null;
    let docOwnerId: number | null = null;
    if (selectedClient) {
      const chosen = clients.find(c => c.id === selectedClient);
      if (chosen) {
        if (chosen.type === 'personal') {
          docOwnerType = 'account_owner';
          docOwnerId = chosen.id;
        } else if (chosen.type === 'family') {
          docOwnerType = 'family_member';
          docOwnerId = chosen.id;
        } else if (chosen.type === 'business') {
          docOwnerType = 'business';
          docOwnerId = chosen.id - 10000; // undo the offset from getClients()
        }
      }
    }

    // Run the chosen extraction engine on one file's bytes → the Filing-field
    // columns to bake into its dms_documents insert. Best-effort: a failed or blank
    // extraction just leaves fields empty (the Filing tab is the review surface).
    // Owner is NEVER set here — it comes only from the owner selector above.
    const buildExtracted = async (
      rawBytes: Uint8Array, gzipBase64: string, fname: string
    ): Promise<Record<string, any>> => {
      try {
        const parsed = await extractFilingFields({
          engine, gzipBase64, rawBytes, mediaType: 'application/pdf', filename: fname,
        });
        const out: Record<string, any> = {};
        if (parsed.partyName) out.party_name = parsed.partyName;
        if (parsed.invoiceRef) out.invoice_ref = parsed.invoiceRef;
        if (parsed.dateOfDocument) out.date_of_document = parsed.dateOfDocument;
        if (parsed.paymentMethod) out.payment_method = parsed.paymentMethod;
        if (parsed.totalAmount) {
          const n = parseFloat(parsed.totalAmount);
          if (!isNaN(n)) out.total_amount = n;
        }
        if (parsed.currency) out.currency = parsed.currency;
        if (parsed.purpose) out.purpose = parsed.purpose;
        const dtId = resolveDocuTypeId(parsed.docTypeHint, docuTypes);
        if (dtId != null) out.docu_type_id = dtId;
        // Content fingerprint for duplicate detection — over the document's text
        // PLUS the extracted Filing fields (so it still works for AI-only extracts
        // that return no rawText). Best-effort; blank signature is fine.
        const sig = contentSignatureFromTokens(buildContentTokens({
          rawText: parsed.rawText,
          partyName: parsed.partyName,
          totalAmount: parsed.totalAmount,
          dateOfDocument: parsed.dateOfDocument,
          invoiceRef: parsed.invoiceRef,
          currency: parsed.currency,
        }));
        if (sig) out.content_minhash = sig;
        return out;
      } catch {
        return {};
      }
    };

    // SHA-256 of the stored bytes — exact-duplicate signal. Never throws to the
    // upload (a hashing failure just leaves file_hash null).
    const fileHashOf = async (bytes: Uint8Array): Promise<string | null> => {
      try { return await sha256Hex(bytes); } catch { return null; }
    };

    for (const qf of queued) {
      setFiles(prev => prev.map(f =>
        f.id === qf.id ? { ...f, status: 'uploading' as const, progress: 10 } : f
      ));

      try {
        const ext = getFileExtension(qf.file.name);
        const isImage = IMAGE_EXTENSIONS.includes(ext);
        const baseName = qf.name.replace(/\.[^.]+$/, '');

        // Step 1: Get PDF bytes (convert image if needed)
        setFiles(prev => prev.map(f =>
          f.id === qf.id ? { ...f, progress: 20 } : f
        ));

        let pdfBytes: Uint8Array;
        if (isImage) {
          pdfBytes = await convertImageToPdfBytes(qf.file);
        } else {
          pdfBytes = new Uint8Array(await qf.file.arrayBuffer());
        }

        // Step 2: Tab-specific handling
        if (activeTab === 'receipts-separate') {
          // --- Separate: split PDF into individual pages ---
          setFiles(prev => prev.map(f =>
            f.id === qf.id ? { ...f, progress: 30 } : f
          ));

          let pageBytesList: Uint8Array[];
          if (isImage) {
            // Image → single-page PDF, nothing to split
            pageBytesList = [pdfBytes];
          } else {
            pageBytesList = await splitPdfIntoPages(pdfBytes);
          }

          let totalStoredSize = 0;
          for (let i = 0; i < pageBytesList.length; i++) {
            const pageBytes = pageBytesList[i];
            const { base64, compressedSize } = await compressBytesToBase64(pageBytes);
            totalStoredSize += compressedSize;

            const now = nowTimestamp();
            // Auto-extract Filing fields and bake them into the SAME insert so the
            // doc keeps created_at === updated_at (still listed as pending to file).
            const extracted = await buildExtracted(pageBytes, base64, qf.name);
            const fileHash = await fileHashOf(pageBytes);
            // Push bytes to the blob store (server-first → /mnt/blobs) the moment
            // processing finishes, so a cleared browser never loses a pending file.
            // When the bytes are durably on the SERVER, don't also store them inline
            // (keeps dms_documents lean); keep inline base64 only on the idb fallback.
            const blobKey = await storeDocBlob(base64, accountOwnerId);
            const inlineBytes = isServerKey(blobKey) ? null : base64;

            await addRow('dms_documents', {
              account_owner_id: accountOwnerId,
              user_id: docUserId,
              dms_folders_id: null,
              dms_folders_lvl1_id: null,
              dms_folders_lvl2_id: null,
              filename: pageBytesList.length > 1
                ? `${baseName}_page${i + 1}.pdf`
                : `${baseName}.pdf`,
              original_filename: qf.name,
              ...extracted,
              file_hash: fileHash,
              file_base64: inlineBytes,
              file_path: blobKey,
              file_size_original: pageBytes.length,
              file_size_stored: compressedSize,
              mime_type: 'application/pdf',
              page_count: 1,
              upload_source: activeTab,
              upload_date: now,
              status: 'Review',
              owner_type: docOwnerType,
              owner_id: docOwnerId,
              notes: notes || null,
              is_active: 1,
              is_deleted: 0,
              created_by: currentUser?.user_id ?? null,
              created_at: now,
              updated_at: now,
            }, 'document_id');

            const pct = 30 + Math.round(((i + 1) / pageBytesList.length) * 60);
            setFiles(prev => prev.map(f =>
              f.id === qf.id ? { ...f, progress: pct } : f
            ));
          }

          setFiles(prev => prev.map(f =>
            f.id === qf.id ? {
              ...f, status: 'done' as const, progress: 100,
              storedSize: totalStoredSize,
              pagesCreated: pageBytesList.length,
            } : f
          ));

        } else {
          // --- Tab 1: Upload as-is (no separation) ---
          setFiles(prev => prev.map(f =>
            f.id === qf.id ? { ...f, progress: 40 } : f
          ));

          // Get page count for PDFs
          let pageCount: number | null = null;
          if (!isImage) {
            try {
              const doc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
              pageCount = doc.getPageCount();
            } catch { /* ignore */ }
          } else {
            pageCount = 1;
          }

          const { base64, compressedSize } = await compressBytesToBase64(pdfBytes);

          setFiles(prev => prev.map(f =>
            f.id === qf.id ? { ...f, progress: 70 } : f
          ));

          const now = nowTimestamp();
          const extracted = await buildExtracted(pdfBytes, base64, qf.name);
          const fileHash = await fileHashOf(pdfBytes);
          const blobKey = await storeDocBlob(base64, accountOwnerId);
          const inlineBytes = isServerKey(blobKey) ? null : base64;

          await addRow('dms_documents', {
            account_owner_id: accountOwnerId,
            user_id: docUserId,
            dms_folders_id: null,
            dms_folders_lvl1_id: null,
            dms_folders_lvl2_id: null,
            filename: isImage ? `${baseName}.pdf` : qf.name,
            original_filename: qf.name,
            ...extracted,
            file_hash: fileHash,
            file_base64: inlineBytes,
            file_path: blobKey,
            file_size_original: qf.size,
            file_size_stored: compressedSize,
            mime_type: 'application/pdf',
            page_count: pageCount,
            upload_source: activeTab,
            upload_date: now,
            status: 'Review',
            owner_type: docOwnerType,
            owner_id: docOwnerId,
            notes: notes || null,
            is_active: 1,
            is_deleted: 0,
            created_by: currentUser?.user_id ?? null,
            created_at: now,
            updated_at: now,
          }, 'document_id');

          setFiles(prev => prev.map(f =>
            f.id === qf.id ? { ...f, status: 'done' as const, progress: 100, storedSize: compressedSize } : f
          ));
        }

        const updatedDocs = await loadTable('dms_documents');
        setDocuments(updatedDocs);

      } catch (err: any) {
        setFiles(prev => prev.map(f =>
          f.id === qf.id ? { ...f, status: 'error' as const, progress: 0, errorMsg: err?.message || 'Upload failed' } : f
        ));
      }
    }

    // All queued files have been processed — nothing left to "save", so the form
    // is no longer dirty (prevents a spurious unsaved-changes prompt on navigation).
    setFormDirty(false);
  }, [files, selectedClient, clients, activeTab, notes, usersData, engine, docuTypes]);

  // Open the delete-confirmation modal for a document. For an original in the
  // separate-upload tab, gather the separated pages that will cascade-delete.
  const requestDelete = useCallback((doc: any) => {
    // A virtual original header (multi-page upload) has no real row — its
    // "children" are ALL the page rows sharing the original filename.
    if (doc.__virtual) {
      const childDocs = documents.filter((d: any) =>
        d.original_filename === doc.original_filename &&
        d.upload_source === 'receipts-separate' &&
        d.is_deleted !== 1
      );
      setDeleteTarget({ doc, childDocs });
      return;
    }
    const isOriginal = doc.upload_source === 'receipts-separate' &&
      doc.filename === doc.original_filename;
    const childDocs = isOriginal
      ? documents.filter((d: any) =>
          d.document_id !== doc.document_id &&
          d.original_filename === doc.original_filename &&
          d.upload_source === 'receipts-separate' &&
          d.account_owner_id === doc.account_owner_id &&
          d.is_deleted !== 1
        )
      : [];
    setDeleteTarget({ doc, childDocs });
  }, [documents]);

  // Soft-delete the modal's target (cascades to separated pages for an original).
  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { doc, childDocs } = deleteTarget;
    // A virtual header has no real document_id — only its page rows get deleted.
    const baseIds = doc.__virtual ? [] : [doc.document_id];
    const idsToDelete = [...baseIds, ...childDocs.map((d: any) => d.document_id)];
    const now = nowTimestamp();

    if (isApiReady()) {
      await Promise.all(
        idsToDelete.map(id =>
          saveRow('dms_documents', id, { is_deleted: 1, status: 'Deleted', updated_at: now }, 'document_id')
        )
      );
      const updatedDocs = await loadTable('dms_documents');
      setDocuments(updatedDocs);
    } else {
      const allDocs = await loadTable('dms_documents');
      const idsSet = new Set(idsToDelete);
      const updated = allDocs.map((d: any) =>
        idsSet.has(d.document_id) ? { ...d, is_deleted: 1, status: 'Deleted', updated_at: now } : d
      );
      saveTableData('dms_documents', updated);
      setDocuments(updated);
    }
    setDeleteTarget(null);
  }, [deleteTarget]);

  // Navigate to Process Docs / Receipts with a document selected
  const goToProcessDoc = useCallback((docId: number) => {
    sessionStorage.setItem('akt_navigate_to_doc', String(docId));
    window.location.hash = 'process-receipts';
  }, []);

  // "Save & continue" = upload the selected files (modal).
  useEffect(() => {
    registerSaveHandler(submitSaver(uploadFiles));
    return () => registerSaveHandler(null);
  }, [uploadFiles]);

  // Auto-upload: in the two "Upload Receipts/Docs" + "Separate & Upload" tabs, files
  // upload as soon as they're added (drop or browse) — no "Upload" button click
  // needed. The ref guard prevents overlapping runs; files added mid-upload are
  // picked up on the next pass once the current batch finishes. (The Convert-to-CSV
  // tab is excluded — it has its own review-grid flow.)
  const autoUploadingRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'receipts-docs' && activeTab !== 'receipts-separate') return;
    if (autoUploadingRef.current) return;
    const queued = files.filter(f => f.status === 'queued');
    if (queued.length === 0) return;
    autoUploadingRef.current = true;
    Promise.resolve(uploadFiles(queued)).finally(() => { autoUploadingRef.current = false; });
  }, [files, activeTab, uploadFiles]);

  const queuedCount = files.filter(f => f.status === 'queued').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const doneCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  // Documents for current tab (not deleted)
  // For separate-upload tab: group originals on top with their pages below
  // A doc stays in the Upload window only while it's still pending — i.e. as
  // uploaded (status 'Review') and not yet filed into a folder. Once it's
  // changed in Process Docs / Receipts (filed / rejected / BK done / moved) it
  // disappears from here and lives in Process Receipts.
  const isPendingDoc = useCallback((d: any) => {
    // Pending = untouched since upload. Any edit in Process Docs/Receipts (file,
    // reject, BK-done, move, set owner) bumps updated_at, dropping it from here.
    if (d.created_at && d.updated_at) return d.updated_at === d.created_at;
    // Fallback when timestamps are missing: as-uploaded status, not yet filed.
    return d.status === 'Review' && (d.dms_folders_id == null || Number(d.dms_folders_id) === 0);
  }, []);

  // Sort comparator value for a doc on the active sort field.
  const sortValueOf = useCallback((d: any) => {
    switch (sortField) {
      case 'filename': return (d.filename || '').toLowerCase();
      case 'page_count': return Number(d.page_count) || 0;
      case 'upload_date':
      default: return d.upload_date || '';
    }
  }, [sortField]);

  const toggleSort = useCallback((field: typeof sortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  // Arrow indicator in a sortable column header: active → direction, else a
  // neutral hint that the column can be sorted.
  const sortArrow = (field: typeof sortField) => (
    <span className={`ud-sort-arrow ${sortField === field ? 'active' : ''}`}>
      {sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
    </span>
  );

  const tabDocs = React.useMemo(() => {
    const docs = documents.filter(d =>
      d.upload_source === activeTab && d.is_deleted !== 1 && isPendingDoc(d)
    );
    const cmp = (a: any, b: any) => {
      const av = sortValueOf(a), bv = sortValueOf(b);
      let r = 0;
      if (av < bv) r = -1; else if (av > bv) r = 1;
      return sortDir === 'asc' ? r : -r;
    };

    if (activeTab !== 'receipts-separate') {
      return [...docs].sort(cmp);
    }

    // Separate-upload: a multi-page upload is saved ONLY as page rows (no real
    // original row). Synthesize a virtual "original" header per group so the user
    // can see and delete the whole document at once (cascading to its pages). A
    // single-page upload already has a real original row (filename === original).
    const extractPageNum = (filename: string): number => {
      const m = filename.match(/_page(\d+)\.pdf$/i);
      return m ? parseInt(m[1], 10) : 0;
    };
    const groups = new Map<string, any[]>();
    docs.forEach(d => {
      const key = d.original_filename || d.filename;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(d);
    });
    const groupList = Array.from(groups.entries()).map(([key, group]) => {
      const realOriginal = group.find(d => d.filename === d.original_filename);
      const pages = group
        .filter(d => d.filename !== d.original_filename)
        .sort((a, b) => extractPageNum(a.filename) - extractPageNum(b.filename));
      let header: any = realOriginal || null;
      if (!header && pages.length > 0) {
        header = {
          __virtual: true,
          document_id: `virt:${key}`,
          filename: key,
          original_filename: key,
          upload_date: pages[0].upload_date,
          created_at: pages[0].created_at,
          page_count: pages.length,
          account_owner_id: pages[0].account_owner_id,
          notes: null,
        };
      }
      return { header, pages, sortDoc: header || pages[0] };
    });
    groupList.sort((a, b) => cmp(a.sortDoc, b.sortDoc));
    const rows: any[] = [];
    groupList.forEach(g => {
      if (g.header) rows.push(g.header);
      rows.push(...g.pages);
    });
    return rows;
  }, [documents, activeTab, isPendingDoc, sortValueOf, sortDir]);

  return (
    <div className="ud-page">
      {/* Page Banner */}
      <div className="ud-page-banner">
        <p><strong>To Manage All Of Your Invoices, Expense Receipts, And All Documents Relating To All Of Your Family, Businesses And Your Clients</strong></p>
        <p><strong>UPLOAD Your Files Below</strong></p>
      </div>

      {/* Tab Bar */}
      <div className="ud-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`ud-tab-btn ${activeTab === tab.key ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
            onClick={async () => {
              if (tab.disabled) return;
              if (!(await confirmDiscardChanges())) return;
              setActiveTab(tab.key);
              setFiles([]);
            }}
            title={tab.disabled ? 'Coming Soon' : undefined}
          >
            {tab.label}
            {tab.disabled && <span className="ud-tab-coming-soon">Coming Soon</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="ud-tab-content">
        {/* Title & Description */}
        <h2 className="ud-title">{tabInfo.title}</h2>
        <p className="ud-description">{tabInfo.description}</p>

        {/* Hint text */}
        <div className="ud-hint-block">
          {tabInfo.hint.split('\n').map((line, i) => (
            <p key={i} className="ud-hint-line">{line}</p>
          ))}
        </div>

        {/* Convert to CSV — bank statement extraction (Claude vision) */}
        {activeTab === 'convert-csv' ? (
          <ConvertToCsvTab clients={clients} />
        ) : isDisabledTab ? (
          <div className="ud-coming-soon-block">
            <div className="ud-coming-soon-icon">🚧</div>
            <h3 className="ud-coming-soon-title">Coming Soon</h3>
            <p className="ud-coming-soon-text">
              This feature is under development. It will allow you to upload bank statements and transaction
              reports to automatically extract data into CSV format for importing into the bookkeeping system.
            </p>
          </div>
        ) : (
          <>
            {/* First-time hint */}
            {showFirstTimeHint && activeTab === 'receipts-docs' && (
              <div className="ud-first-time-hint">
                <p className="ud-first-time-text">
                  IF THIS IS YOUR FIRST TIME UPLOADING FILES HERE, PLEASE UPLOAD A SAMPLE FILE TO TRY FIRST;
                  THEN GO TO PROCESS RECEIPTS AND SEE IF THIS IS WHAT YOU NEED. IF NOT, YOU CAN DELETE THE INDIVIDUAL PAGES THERE.
                </p>
                <button className="ud-dismiss-hint" onClick={() => setShowFirstTimeHint(false)} title="Dismiss">✕</button>
              </div>
            )}

            {/* Owner Selector */}
            <div className="ud-client-section">
              <label className="ud-client-label">Choose Owner of the document(s)</label>
              <SearchableDropdown
                className="ud-client-select"
                options={[
                  ...clients.filter(c => c.type === 'personal').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Account Owners',
                  })),
                  ...clients.filter(c => c.type === 'family').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Family Members',
                  })),
                  ...clients.filter(c => c.type === 'business').map(c => ({
                    value: String(c.id),
                    label: c.name,
                    group: 'Businesses',
                  })),
                ]}
                value={selectedClient != null ? String(selectedClient) : ''}
                onChange={(val) => setSelectedClient(val ? Number(val) : null)}
                placeholder="-- Optional: Select an Owner --"
              />
            </div>

            {/* Auto-fill extraction engine */}
            <div className="ud-engine-section">
              <label className="ud-client-label">Auto-fill the Filing tab from the document</label>
              <div className="ud-engine-toggle" role="group" aria-label="Auto-fill engine">
                <button
                  type="button"
                  className={`ud-engine-btn ${engine === 'auto' ? 'active' : ''}`}
                  onClick={() => changeEngine('auto')}
                >
                  Auto
                </button>
                <button
                  type="button"
                  className={`ud-engine-btn ${engine === 'local' ? 'active' : ''}`}
                  onClick={() => changeEngine('local')}
                >
                  Free &middot; Browser
                </button>
                <button
                  type="button"
                  className={`ud-engine-btn ${engine === 'claude' ? 'active' : ''}`}
                  onClick={() => changeEngine('claude')}
                >
                  Claude AI
                </button>
                {process.env.REACT_APP_SELF_HOSTED_EXTRACT === '1' && (
                  <button
                    type="button"
                    className={`ud-engine-btn ${engine === 'self_hosted' ? 'active' : ''}`}
                    onClick={() => changeEngine('self_hosted')}
                  >
                    Self-hosted
                  </button>
                )}
              </div>
              <p className="ud-engine-hint">
                {engine === 'auto' && "Smart & low-cost — reads every document free in your browser first, and sends only the few it can't confidently read (logo-only vendors, scans) to AI. You review everything in the Filing tab."}
                {engine === 'local' && "Free & private — browser-only text + OCR, no AI. Scans and logo-only vendors may extract little. You review everything in the Filing tab."}
                {engine === 'claude' && "Claude AI reads every document (scans, photos, logos) for the most accurate auto-fill — small per-document cost. You review everything in the Filing tab."}
                {engine === 'self_hosted' && "Your self-hosted vision model reads every document — no per-document API cost once your GPU endpoint is configured."}
              </p>
            </div>

            {/* Drop Zone */}
            <div
              className={`ud-dropzone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_INPUT}
                multiple
                onChange={handleFileInputChange}
                className="ud-file-input"
              />
              <div className="ud-dropzone-icon">{isDragging ? '📥' : '📄'}</div>
              <p className="ud-dropzone-title">
                {isDragging ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="ud-dropzone-subtitle">
                or <span className="ud-browse-link">browse your computer</span>
              </p>
              <p className="ud-dropzone-meta">
                PDF, PNG, JPEG/JPG &middot; Max {formatFileSize(MAX_FILE_SIZE)} per file &middot; Images are converted to PDF &middot; All files are gzip-compressed
                {activeTab === 'receipts-separate' && (
                  <span> &middot; <strong>Each PDF page saved as a separate document</strong></span>
                )}
              </p>
            </div>

            {/* File Queue */}
            {files.length > 0 && (
              <div className="ud-file-queue">
                <div className="ud-queue-header">
                  <h3 className="ud-queue-title">
                    Files ({files.length})
                    {queuedCount > 0 && <span className="ud-badge ud-badge-queued">{queuedCount} ready</span>}
                    {uploadingCount > 0 && <span className="ud-badge ud-badge-uploading">{uploadingCount} uploading</span>}
                    {doneCount > 0 && <span className="ud-badge ud-badge-done">{doneCount} done</span>}
                    {errorCount > 0 && <span className="ud-badge ud-badge-error">{errorCount} error</span>}
                  </h3>
                  <div className="ud-queue-actions">
                    <button className="ud-btn ud-btn-secondary" onClick={clearAllFiles}>Clear All</button>
                    <button
                      className="ud-btn ud-btn-primary"
                      onClick={() => uploadFiles()}
                      disabled={queuedCount === 0 || uploadingCount > 0}
                    >
                      {uploadingCount > 0 ? 'Uploading...' : `Upload ${queuedCount > 0 ? `(${queuedCount})` : ''}`}
                    </button>
                  </div>
                </div>

                <div className="ud-file-list">
                  {files.map(f => (
                    <div key={f.id} className={`ud-file-item ud-file-${f.status}`}>
                      <div className="ud-file-icon">
                        {f.status === 'done' ? '✅' : f.status === 'error' ? '❌' : f.status === 'uploading' ? '⏳' : (f.convertedFrom ? '🖼️' : '📄')}
                      </div>
                      <div className="ud-file-info">
                        <div className="ud-file-name">
                          {f.name}
                          {f.convertedFrom && f.status !== 'done' && (
                            <span className="ud-file-convert-tag">{f.convertedFrom} → PDF</span>
                          )}
                        </div>
                        <div className="ud-file-meta">
                          {formatFileSize(f.size)}
                          {f.storedSize != null && f.storedSize < f.size && (
                            <span className="ud-file-compressed">
                              {' '}&rarr; {formatFileSize(f.storedSize)} compressed ({Math.round((1 - f.storedSize / f.size) * 100)}% saved)
                            </span>
                          )}
                          {f.pagesCreated != null && f.pagesCreated > 0 && (
                            <span className="ud-file-pages-msg">
                              {' '}&mdash; {f.pagesCreated} {f.pagesCreated === 1 ? 'page' : 'pages'} separated and saved
                            </span>
                          )}
                          {f.convertedFrom && f.status === 'done' && (
                            <span className="ud-file-converted-msg"> &mdash; Converted from {f.convertedFrom} to PDF</span>
                          )}
                          {f.errorMsg && <span className="ud-file-error-msg"> &mdash; {f.errorMsg}</span>}
                          {f.status === 'done' && !f.errorMsg && !f.pagesCreated && (
                            <span className="ud-file-done-msg"> &mdash; Saved to dms_documents</span>
                          )}
                        </div>
                        {f.status === 'uploading' && (
                          <div className="ud-progress-bar">
                            <div className="ud-progress-fill" style={{ width: `${f.progress}%` }} />
                          </div>
                        )}
                      </div>
                      <button
                        className="ud-file-remove"
                        onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {queuedCount > 0 && (
              <div className="ud-notes-section">
                <label className="ud-notes-label">Notes (optional)</label>
                <textarea
                  className="ud-notes-input"
                  placeholder="Add any notes about these uploads..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                />
              </div>
            )}

            {/* Previously Uploaded Documents for this tab */}
            {tabDocs.length > 0 && (
              <div className="ud-history">
                <h3 className="ud-history-title">Uploaded Documents ({tabDocs.length})</h3>
                <div className="ud-history-table-wrap">
                <table className="ud-history-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th className="ud-sortable" onClick={() => toggleSort('filename')}>File Name{sortArrow('filename')}</th>
                      {activeTab === 'receipts-docs' && (
                        <th className="ud-sortable" onClick={() => toggleSort('page_count')}>Pages{sortArrow('page_count')}</th>
                      )}
                      <th className="ud-sortable" onClick={() => toggleSort('upload_date')}>Uploaded{sortArrow('upload_date')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabDocs.map((d, idx) => {
                      const isVirtual = !!d.__virtual;
                      const isOriginal = activeTab === 'receipts-separate' && d.filename === d.original_filename;
                      const isPage = activeTab === 'receipts-separate' && d.filename !== d.original_filename;
                      return (
                        <tr key={d.document_id} className={isOriginal ? 'ud-row-original' : isPage ? 'ud-row-page' : ''}>
                          <td className="ud-history-id">{idx + 1}</td>
                          <td className="ud-history-name">
                            <span className="ud-history-icon">{isOriginal ? '📋' : '📄'}</span>
                            {isVirtual ? (
                              <span className="ud-history-name-plain" title="Multi-page document — delete removes all its pages">
                                {d.filename}
                                <span className="ud-page-count-hint"> ({d.page_count} page{d.page_count !== 1 ? 's' : ''})</span>
                              </span>
                            ) : (
                            <span
                              className="ud-history-name-link"
                              onClick={() => goToProcessDoc(d.document_id)}
                              title="Click to open in Process Docs / Receipts"
                            >
                              {isPage && <span className="ud-page-indent">↳ </span>}
                              {d.filename}
                            </span>
                            )}
                          </td>
                          {activeTab === 'receipts-docs' && (
                            <td>{d.page_count ?? '—'}</td>
                          )}
                          <td>{formatDate(d.upload_date)}</td>
                          <td>
                            <button
                              className="ud-history-delete"
                              onClick={(e) => { e.stopPropagation(); requestDelete(d); }}
                              title={isVirtual ? 'Delete this document & all its pages' : isOriginal ? 'Delete original & all separated pages' : 'Delete'}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Big, bold delete-confirmation warning */}
      {deleteTarget && (() => {
        const dt = deleteTarget;
        const n = dt.childDocs.length;
        const isCascade = n > 0;                       // deleting a parent + its pages
        const total = (dt.doc.__virtual ? 0 : 1) + n;  // total docs that get deleted
        return (
          <div className="ud-delete-overlay" onClick={() => setDeleteTarget(null)}>
            <div className="ud-delete-modal" onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true">
              <div className="ud-delete-icon">⚠️</div>
              <div className="ud-delete-title">Delete this document?</div>
              <div className="ud-delete-body">
                {isCascade ? (
                  <p>
                    You are about to delete the multi-page document <strong>“{dt.doc.original_filename}”</strong> and all{' '}
                    <strong>{n}</strong> separated page{n !== 1 ? 's' : ''}.<br />
                    <strong className="ud-delete-emphasis">This permanently removes them from the system — forever.</strong>
                  </p>
                ) : (
                  <p>
                    You are about to delete <strong>“{dt.doc.filename}”</strong>.<br />
                    <strong className="ud-delete-emphasis">This removes it from the system.</strong>
                  </p>
                )}
              </div>
              <div className="ud-delete-actions">
                <button className="ud-delete-cancel-btn" onClick={() => setDeleteTarget(null)}>Cancel</button>
                <button className="ud-delete-confirm-btn" onClick={confirmDelete}>
                  {isCascade ? `Delete all ${total}` : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default UploadDocsPage;
